export const safeParseUser = () => {
  const raw = window.localStorage.getItem("user");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const normalizeRoleName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const normalizeString = (value) => String(value ?? "").trim();

const normalizeBool = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  return fallback;
};

const pickString = (...values) => values.map((value) => normalizeString(value)).find(Boolean) || "";

const pickNullableString = (...values) => {
  const value = values.map((item) => normalizeString(item)).find(Boolean);
  return value || null;
};

const pickNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const next = Number(value);
    if (!Number.isNaN(next)) return next;
  }

  return 0;
};

export const getUserRoleNames = (user) => {
  if (!user) return [];

  const fromArray = Array.isArray(user.roleNames)
    ? user.roleNames
    : Array.isArray(user.RoleNames)
      ? user.RoleNames
      : [];

  if (fromArray.length) {
    return fromArray.map((x) => String(x || "").trim()).filter(Boolean);
  }

  const single = user.roleName || user.RoleName || user.role || user.Role;
  return single ? [String(single).trim()] : [];
};

export const getCurrentUserIdentity = () => {
  const user = safeParseUser();
  const roleNames = getUserRoleNames(user);

  return {
    raw: user,
    userId: user?.userId || user?.UserId || user?.id || user?.Id || "",
    fullName: user?.fullName || user?.FullName || user?.name || user?.Name || "",
    email: user?.email || user?.Email || "",
    roleNames,
    normalizedRoles: roleNames.map(normalizeRoleName),
  };
};

export const isCustomerIdentity = (identity) => {
  return (identity?.normalizedRoles || []).some((role) => role === "customer" || role === "customer user");
};

export const isCustomerAdminIdentity = (identity) => {
  return (identity?.normalizedRoles || []).some((role) => role === "customer");
};

export const isCustomerUserIdentity = (identity) => {
  return (identity?.normalizedRoles || []).some((role) => role === "customer user");
};

export const isEmployeeIdentity = (identity) => {
  return (identity?.normalizedRoles || []).some((role) => role.includes("employee"));
};

export const isStaffIdentity = (identity) => {
  return (identity?.normalizedRoles || []).some((role) => {
    return role.includes("admin") || role.includes("support") || role.includes("help") || role.includes("manager") || role.includes("hr") || role.includes("employee");
  });
};

export const getIdentityRoleLabel = (identity) => {
  if (isCustomerUserIdentity(identity)) return "Customer User";
  if (isCustomerAdminIdentity(identity)) return "Customer";
  if (isEmployeeIdentity(identity)) return "Employee";
  if (isStaffIdentity(identity)) return "Staff";
  return "User";
};

export const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export const buildMediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const relative = String(path).replace(/^~\//, "").replace(/^\/+/, "");
  const apiBase = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const origin = apiBase.replace(/\/api\/v1$/i, "").replace(/\/api$/i, "");

  return origin ? `${origin}/${relative}` : `/${relative}`;
};

export const normalizeTicketConversationItem = (item, ticket = null) => ({
  id: pickString(item?.id, item?.Id, `${ticket?.id || "ticket"}-${item?.createdAt || item?.CreatedAt || Math.random()}`),
  isInitialMessage: normalizeBool(item?.isInitialMessage ?? item?.IsInitialMessage, false),
  userId: pickString(item?.userId, item?.UserId),
  senderName: pickString(item?.senderName, item?.SenderName, item?.userName, item?.UserName, "User"),
  senderEmail: pickNullableString(item?.senderEmail, item?.SenderEmail, item?.userEmail, item?.UserEmail),
  senderType: pickString(item?.senderType, item?.SenderType, "Update"),
  message: pickNullableString(item?.message, item?.Message),
  attachmentUrl: pickNullableString(item?.attachmentUrl, item?.AttachmentUrl),
  status: pickNullableString(item?.status, item?.Status),
  createdAt: item?.createdAt ?? item?.CreatedAt ?? null,
});

export const normalizeTicketData = (ticket) => {
  const normalized = {
    raw: ticket,
    id: pickNumber(ticket?.id, ticket?.Id),
    ticketNumber: pickString(ticket?.ticketNumber, ticket?.TicketNumber),
    subject: pickString(ticket?.subject, ticket?.Subject),
    category: pickString(ticket?.category, ticket?.Category),
    priority: pickString(ticket?.priority, ticket?.Priority),
    status: pickString(ticket?.status, ticket?.Status),
    contactEmail: pickString(ticket?.contactEmail, ticket?.ContactEmail),
    userId: pickString(ticket?.userId, ticket?.UserId, ticket?.requesterUserId, ticket?.RequesterUserId),
    userName: pickString(ticket?.userName, ticket?.UserName, ticket?.requesterName, ticket?.RequesterName),
    domainUrl: pickNullableString(ticket?.domainUrl, ticket?.DomainUrl),
    createdAt: ticket?.createdAt ?? ticket?.CreatedAt ?? null,
    lastReplyAt: ticket?.lastReplyAt ?? ticket?.LastReplyAt ?? null,
    isAdminCreate: normalizeBool(ticket?.isAdminCreate ?? ticket?.IsAdminCreate, false),
    lastSeenAt: ticket?.lastSeenAt ?? ticket?.LastSeenAt ?? null,
    canManageStatus: normalizeBool(ticket?.canManageStatus ?? ticket?.CanManageStatus, false),
    canReply: normalizeBool(ticket?.canReply ?? ticket?.CanReply, true),
    hasUnread: normalizeBool(ticket?.hasUnread ?? ticket?.HasUnread, false),
    unreadCount: pickNumber(ticket?.unreadCount, ticket?.UnreadCount),
    organizationId: pickNumber(ticket?.organizationId, ticket?.OrganizationId),
    organizationName: pickNullableString(ticket?.organizationName, ticket?.OrganizationName, ticket?.orgName, ticket?.OrgName),
    customerId: pickNumber(ticket?.customerId, ticket?.CustomerId),
    customerName: pickNullableString(ticket?.customerName, ticket?.CustomerName),
    customerCode: pickNullableString(ticket?.customerCode, ticket?.CustomerCode),
    requesterUserId: pickString(ticket?.requesterUserId, ticket?.RequesterUserId, ticket?.userId, ticket?.UserId),
    requesterName: pickString(ticket?.requesterName, ticket?.RequesterName, ticket?.userName, ticket?.UserName),
    requesterEmail: pickNullableString(ticket?.requesterEmail, ticket?.RequesterEmail, ticket?.contactEmail, ticket?.ContactEmail),
    description: pickNullableString(ticket?.description, ticket?.Description),
    attachmentUrl: pickNullableString(ticket?.attachmentUrl, ticket?.AttachmentUrl),
  };

  normalized.conversation = Array.isArray(ticket?.conversation || ticket?.Conversation)
    ? (ticket?.conversation || ticket?.Conversation).map((item) => normalizeTicketConversationItem(item, normalized))
    : [];

  return normalized;
};

export const isOwnTicketForIdentity = (ticket, identity) => {
  const currentUserId = normalizeString(identity?.userId);
  if (!currentUserId) return false;

  const requesterUserId = normalizeString(ticket?.requesterUserId || ticket?.userId);
  return requesterUserId === currentUserId;
};

export const getStatusClasses = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "resolved") {
    return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  }

  if (normalized === "in progress") {
    return "bg-blue-100 text-blue-700 border border-blue-200";
  }

  if (normalized === "on hold") {
    return "bg-amber-100 text-amber-700 border border-amber-200";
  }

  return "bg-slate-100 text-slate-700 border border-slate-200";
};

export const getPriorityClasses = (priority) => {
  const normalized = String(priority || "").toLowerCase();

  if (normalized === "high") {
    return "bg-rose-100 text-rose-700 border border-rose-200";
  }

  if (normalized === "medium") {
    return "bg-orange-100 text-orange-700 border border-orange-200";
  }

  return "bg-green-100 text-green-700 border border-green-200";
};
